/**
 * Frontend Patient Request Manager
 * Handles patient enrollment request operations
 */
import { apiClient } from './apiClient';

export class PatientRequestManager {
  constructor() {
    this.useMockData = import.meta.env.MODE === 'development' && !import.meta.env.VITE_API_ENDPOINT;
  }

  /**
   * Create a new patient request
   * @param {string} clinicId - The clinic ID
   * @param {Object} patientData - Patient information
   * @returns {Promise<Object>} Result with requestId
   */
  async createRequest(clinicId, patientData) {
    if (this.useMockData) {
      console.log('Mock: Would create patient request:', { clinicId, patientData });
      return {
        success: true,
        requestId: `mock-${Date.now()}`,
        status: 'pending',
        emailNotification: { sent: true }
      };
    }

    try {
      const data = await apiClient.patientRequests({
        operation: 'createPatientRequest',
        clinicId,
        patientData
      });
      return data;
    } catch (error) {
      console.error('Error creating patient request:', error);
      throw new Error(`Failed to create request: ${error.message}`);
    }
  }

  /**
   * List patient requests by clinic and optional status
   * @param {string} clinicId - The clinic ID (or '*' for all clinics - super admin only)
   * @param {string} status - Optional status filter ('pending', 'approved', 'rejected')
   * @param {number} limit - Maximum number of requests to return
   * @returns {Promise<Object>} List of requests
   */
  async listRequests(clinicId = null, status = null, limit = 50) {
    if (this.useMockData) {
      return {
        success: true,
        requests: [
          {
            requestId: 'mock-1',
            clinicId: 'clinic-1',
            status: 'pending',
            requestedAt: new Date().toISOString(),
            requestedByEmail: 'user@example.com',
            patientData: {
              idNumber: 'PAT-001',
              gender: 'MALE',
              yearOfBirth: '1980'
            }
          }
        ],
        count: 1
      };
    }

    try {
      const data = await apiClient.patientRequests({
        operation: 'listPatientRequests',
        clinicId,
        status,
        limit
      });
      return data;
    } catch (error) {
      console.error('Error listing patient requests:', error);
      throw new Error(`Failed to list requests: ${error.message}`);
    }
  }

  /**
   * Get a single patient request
   * @param {string} requestId - The request ID
   * @returns {Promise<Object>} Request details
   */
  async getRequest(requestId) {
    if (this.useMockData) {
      return {
        success: true,
        request: {
          requestId,
          clinicId: 'clinic-1',
          status: 'pending',
          requestedAt: new Date().toISOString(),
          requestedByEmail: 'user@example.com',
          patientData: {
            idNumber: 'PAT-001',
            gender: 'MALE',
            yearOfBirth: '1980'
          }
        }
      };
    }

    try {
      const data = await apiClient.patientRequests({
        operation: 'getPatientRequest',
        requestId
      });
      return data;
    } catch (error) {
      console.error(`Error getting patient request ${requestId}:`, error);
      throw new Error(`Failed to get request: ${error.message}`);
    }
  }

  /**
   * Update an existing pending patient request
   * @param {string} requestId - The request ID
   * @param {Object} patientData - Updated patient information
   * @returns {Promise<Object>} Result
   */
  async updateRequest(requestId, patientData) {
    if (this.useMockData) {
      console.log('Mock: Would update patient request:', { requestId, patientData });
      return {
        success: true,
        requestId,
        status: 'pending',
        emailNotification: { sent: true }
      };
    }

    try {
      const data = await apiClient.patientRequests({
        operation: 'updatePatientRequest',
        requestId,
        patientData
      });
      return data;
    } catch (error) {
      console.error(`Error updating patient request ${requestId}:`, error);
      throw new Error(`Failed to update request: ${error.message}`);
    }
  }

  /**
   * Update request status (approve/reject, or reopen an approved request) - Super admin only
   * @param {string} requestId - The request ID
   * @param {string} status - New status ('approved', 'rejected' or 'pending' to reopen)
   * @param {string} reason - Optional reason for the status change
   * @returns {Promise<Object>} Result
   */
  async updateStatus(requestId, status, reason = null) {
    if (!['approved', 'rejected', 'pending'].includes(status)) {
      throw new Error('Status must be "approved", "rejected" or "pending"');
    }

    if (this.useMockData) {
      console.log('Mock: Would update request status:', { requestId, status, reason });
      return { success: true, requestId, status };
    }

    try {
      const data = await apiClient.patientRequests({
        operation: 'updateRequestStatus',
        requestId,
        status,
        reason
      });
      return data;
    } catch (error) {
      console.error(`Error updating request status ${requestId}:`, error);
      throw new Error(`Failed to update status: ${error.message}`);
    }
  }

  /**
   * Cancel own patient request
   * @param {string} requestId - The request ID
   * @returns {Promise<Object>} Result
   */
  async cancelRequest(requestId) {
    if (this.useMockData) {
      console.log('Mock: Would cancel request:', requestId);
      return { success: true, message: 'Request cancelled successfully' };
    }

    try {
      const data = await apiClient.patientRequests({
        operation: 'cancelPatientRequest',
        requestId
      });
      return data;
    } catch (error) {
      console.error(`Error cancelling patient request ${requestId}:`, error);
      throw new Error(`Failed to cancel request: ${error.message}`);
    }
  }

  /**
   * Get status display properties
   * @param {string} status - The request status
   * @returns {Object} Display properties { color, label }
   */
  getStatusDisplay(status) {
    switch (status) {
      case 'pending':
        return { color: 'warning', label: 'Pending' };
      case 'approved':
        return { color: 'success', label: 'Approved' };
      case 'rejected':
        return { color: 'error', label: 'Rejected' };
      case 'cancelled':
        return { color: 'default', label: 'Cancelled' };
      default:
        return { color: 'default', label: status };
    }
  }

  /**
   * Format date for display
   * @param {string} isoDate - ISO date string
   * @returns {string} Formatted date
   */
  formatDate(isoDate) {
    if (!isoDate) return 'N/A';
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}

// Export singleton instance
export const patientRequestManager = new PatientRequestManager();
